import { useState, useMemo, useRef } from "react";
import MainBannerBg from "./../../assets/images/header-bg.svg?react";
import { ContactUsUrl, HomeUrl, SignUpUrl } from "../../utils/urls";
import { Link } from "react-router-dom";
import { Button, cn, Flex, Typography, useDjunoDesign } from "djuno-design";
import Container, { ContainerFluid } from "../layouts/Container";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticate } from "../../store/auth/authSlice";

import MicrosoftAzureIcon from "./../../assets/icons/socials/microsoft-azure-color.svg?react";
import GoogleCloudIcon from "./../../assets/icons/socials/google-cloud-color.svg?react";
import AwsIcon from "./../../assets/icons/socials/amazon-color.svg?react";
import OvhIcon from "./../../assets/icons/socials/ovh-color.svg?react";

import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";

const IndexBanner = () => {
  const isAuth = useAppSelector(selectIsAuthenticate);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const carouselRef = useRef<any>(null);

  const slides = [
    {
      title: "Beyond Cloud",
      subtitle: "Djuno Multi-Cloud, Multi-Environment Management Platform",
      showGetStarted: false,
    },
    {
      title: "Zero-Ops Intelligent Web3.0 Development platform",
      subtitle:
        "Djuno web3.0 development platform offers a complete stack for enterprise blockchain provided as a low cost low code platform",
      showGetStarted: true,
    },
  ];

  const onLoadedData = () => {
    setIsVideoLoaded(true);
  };

  const bannerItems = useMemo(
    () =>
      slides.map((item, index) => (
        <div
          className="min-h-[300px] lg:min-h-[240px] flex flex-col px-1"
          data-value={index}
          key={index}
        >
          <div className="flex flex-grow min-h-[100px] md:min-h-[150px] ">
            <Flex direction="col" className="flex-1 justify-end gap-3">
              <Typography.Text className="font-semibold !text-lg md:!text-2xl lg:!text-3xl drop-shadow-lg lg:!drop-shadow-none !text-slate-50 md:!text-slate-800 dark:!text-slate-50">
                {item.title}
              </Typography.Text>

              <Typography.Text
                uiType="secondary"
                className="w-fit !text-sm md:!text-lg !text-slate-50 md:!text-slate-800 dark:!text-slate-50 md:backdrop-blur-[2px] md:!rounded-3xl dark:md:!backdrop-blur-0"
              >
                {item.subtitle}
              </Typography.Text>
            </Flex>
          </div>

          <div className="mt-auto">
            <div className="mt-6 md:mt-3 mb-2 max-[425px]:mt-10">
              <div className="grid grid-cols-12 gap-4 w-full md:max-w-[450px] mx-auto lg:mx-0">
                <Link to={ContactUsUrl} className={"col-span-6"}>
                  <Button
                    uiType="primary"
                    className="!w-full !justify-center !bg-white/20 !backdrop-blur-sm md:!bg-transparent"
                  >
                    <Typography.Text className="!font-semibold !text-white md:!text-primary-500 !text-sm md:!text-md">
                      Contact sales
                    </Typography.Text>
                  </Button>
                </Link>

                {item.showGetStarted && (
                  <Link
                    to={isAuth ? HomeUrl : SignUpUrl}
                    className="col-span-6"
                  >
                    <Button
                      uiType="primary"
                      className="!w-full !justify-center"
                    >
                      <Typography.Text
                        className="!font-semibold !text-sm md:!text-md"
                        uiType="transparent"
                      >
                        Get started
                      </Typography.Text>
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Indicators */}
            {/* <div className="flex justify-center gap-2 my-6">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => carouselRef.current?.slideTo(idx)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    carouselRef.current?.state?.activeIndex === idx
                      ? "bg-blue-600"
                      : "bg-blue-300"
                  }`}
                />
              ))}
            </div> */}
          </div>
        </div>
      )),
    [isAuth]
  );

  return (
    <div className="mb-8 sm:mb-12 lg:mb-24 xl:mb-32 relative h-[500px] md:h-[437px] lg:h-[570px] xl:h-[700px] mt-[80px] lg:mt-0">
      <ContainerFluid className="relative inset-0 !h-full">
        <div
          className={cn(
            "h-full absolute z-0 right-0 top-0 before:absolute before:w-full before:h-full before:opacity-50 before:!overflow-hidden",
            { "before:bg-image-cover-gradient": isVideoLoaded }
          )}
        >
          <video
            className={cn(" w-full h-full object-cover", {
              "opacity-100": isVideoLoaded,
            })}
            id="video"
            autoPlay
            playsInline
            loop
            muted
            onLoadedData={onLoadedData}
          >
            <source src="https://webcdn.djuno.io/cdn/loopvideo.mov" />
          </video>
        </div>
        <MainBannerBg className="!h-[102%] translate-x-1 overflow-hidden absolute z-10 -top-1 hidden md:block text-white dark:text-dark-1 -right-12 lg:-right-16 xl:-right-20" />
      </ContainerFluid>
      <Container className="absolute inset-0 w-full z-50 pt-[0px] md:pt-[30px] lg:pt-[150px] xl:pt-[210px]">
        <div className="header-button ">
          <div className="grid grid-cols-12 gap-1">
            <div className="col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-6">
              <AliceCarousel
                ref={carouselRef}
                autoPlay
                autoPlayInterval={10000}
                animationDuration={1500}
                animationType="slide"
                infinite
                mouseTracking
                disableButtonsControls
                items={bannerItems}
              />

              <Flex
                direction="col"
                className="gap-2 rounded-xl p-4 border border-white/10 backdrop-blur-sm bg-gradient-to-tr from-white/50 to-white/5 backdrop-brightness-150 backdrop-contrast-100 shadow-xl md:border-none md:backdrop-blur-none md:bg-transparent md:backdrop-brightness-100 md:shadow-none md:bg-none"
              >
                <Typography.Text className="font-semibold !text-sm md:!text-md lg:!text-lg drop-shadow-lg lg:!drop-shadow-none !text-slate-800 dark:md:!text-slate-50 mb-1">
                  Powered By
                </Typography.Text>
                <div className="grid grid-cols-12 xl:!flex xl:items-center gap-3 md:gap-5">
                  <MicrosoftAzureIcon className="h-5 flex-shrink-0 col-span-6 lg:col-span-3" />
                  <AwsIcon className="h-5 flex-shrink-0 text-[#252F3E] dark:md:text-[#778394] col-span-6 lg:col-span-2" />
                  <OvhIcon className="h-4 flex-shrink-0 text-[#000e9c] dark:md:text-[#4186c7] col-span-6 lg:col-span-4" />
                  <GoogleCloudIcon className="h-5 flex-shrink-0 col-span-6 lg:col-span-3" />
                </div>
              </Flex>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export const ProductHuntButton: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { theme } = useDjunoDesign();

  return (
    <div className={className}>
      {theme.mode === "dark" ? (
        <a
          href="https://www.producthunt.com/products/djuno/reviews?utm_source=badge-product_review&utm_medium=badge&utm_souce=badge-djuno"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=992227&theme=dark"
            alt="Djuno - All&#0045;in&#0045;one&#0032;Web3&#0032;cloud&#0032;and&#0032;low&#0032;code&#0032;developer&#0032;platform | Product Hunt"
            className="w-full"
          />
        </a>
      ) : (
        <a
          href="https://www.producthunt.com/posts/djuno?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-djuno"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=886423&theme=neutral&t=1739963766486"
            alt="Djuno - All&#0045;in&#0045;one&#0032;Web3&#0032;cloud&#0032;and&#0032;low&#0032;code&#0032;developer&#0032;platform | Product Hunt"
            className="w-full"
          />
        </a>
      )}
    </div>
  );
};

export default IndexBanner;
